import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowRight, Boxes } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState } from '@/components/data/EmptyState';
import { findProvider } from '@/config/providers';
import { useEntityCounts } from '@/hooks/useProviderEntities';
import { absoluteStamp, formatNumber, formatRelative } from '@/lib/utils';

export function ProviderRoute() {
  // All hooks must run BEFORE any conditional return so the call order is
  // stable across renders (Rules of Hooks). Pass the maybe-null provider
  // to useEntityCounts; the hook is responsible for gating its fetch on
  // !!provider.
  const { providerKey = '' } = useParams();
  const provider = findProvider(providerKey);
  const counts = useEntityCounts(provider);

  if (!provider) return <Navigate to="/" replace />;

  return (
    <>
      <PageHeader
        title={provider.label}
        description={provider.tagline}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="info">schema: {provider.schema}</Badge>
            <Badge variant="outline">queue: {provider.hangfireQueue}</Badge>
            {provider.sports.map((s) => (
              <Badge key={s} variant="outline" className="capitalize">{s}</Badge>
            ))}
          </div>
        }
      />

      {provider.key === 'presto' && <Link to="/pbp/presto" className="mb-5 block rounded-lg border border-primary/30 bg-primary/5 p-4 text-primary hover:underline focus-visible:outline">Play-by-play · Coverage and game feeds →</Link>}
      <Card>
        <CardHeader>
          <CardTitle>Entities</CardTitle>
          <CardDescription>
            Browse each table in <span className="font-mono">{provider.schema}.*</span>. Counts run as a HEAD request against PostgREST.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {counts.isError ? (
            <ErrorState error={counts.error} />
          ) : counts.isLoading ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {provider.entities.map((e) => <Skeleton key={e.key} className="h-24 w-full" />)}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {provider.entities.map((e) => {
                const c = counts.data?.find((x) => x.entity === e.key);
                return (
                  <Link
                    key={e.key}
                    to={`/entities/${provider.key}/${e.key}`}
                    className="group rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-card/80"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Boxes className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                        <span className="text-sm font-medium">{e.label}</span>
                      </div>
                      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                    </div>
                    <div className="mt-2 flex items-end justify-between">
                      <div className="text-2xl font-semibold tabular-nums">
                        {c?.error ? '—' : c?.count != null ? formatNumber(c.count) : '—'}
                      </div>
                      <span className="font-mono text-[11px] text-muted-foreground">{provider.schema}.{e.table}</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2 border-t border-border/60 pt-2 text-[11px] text-muted-foreground">
                      <span title={`Last harvested: ${absoluteStamp(c?.lastHarvested)}`}>
                        Harvested <span className="text-foreground/80">{formatRelative(c?.lastHarvested)}</span>
                      </span>
                      <span title={`Last synced: ${absoluteStamp(c?.lastSynced)}`}>
                        Synced <span className="text-foreground/80">{formatRelative(c?.lastSynced)}</span>
                      </span>
                    </div>
                    {c?.error && (
                      <div className="mt-1 truncate text-[11px] text-destructive" title={c.error}>{c.error}</div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}
